// 구인 공고 목록 페이지
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  Badge,
  Select,
  Stack,
  Heading,
  useToast,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  Tag,
  TagLabel,
  TagCloseButton,
  HStack,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, SettingsIcon } from '@chakra-ui/icons';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import { getJobPostings, getLocations, saveLocations } from 'utils/firestoreUtils';
import { useAuth } from 'contexts/AuthContext';
import { useHistory } from 'react-router-dom';
import jobTypes from 'constants/jobTypes';

function truncateDescription(text, limit = 80) {
  if (!text) return '';
  return text.length > limit ? `${text.slice(0, limit)}...` : text;
}

function JobPostings() {
  const [jobPostings, setJobPostings] = useState([]);
  const [filteredPostings, setFilteredPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  const [newLocation, setNewLocation] = useState('');
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { userProfile } = useAuth();
  const history = useHistory();
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    loadJobPostings();
    loadLocations();
  }, []);

  useEffect(() => {
    filterPostings();
  }, [jobPostings, statusFilter, locationFilter, jobTypeFilter]);

  async function loadJobPostings() {
    try {
      setLoading(true);
      const data = await getJobPostings();
      setJobPostings(data);
    } catch (error) {
      console.error('공고 로딩 오류:', error);
      toast({
        title: '오류',
        description: '공고를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }

  async function loadLocations() {
    try {
      const locationList = await getLocations();
      setLocations(locationList);
    } catch (error) {
      console.error('위치 로딩 오류:', error);
    }
  }

  function addLocation() {
    if (!newLocation.trim()) {
      toast({
        title: '입력 오류',
        description: '위치명을 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (locations.includes(newLocation.trim())) {
      toast({
        title: '중복 오류',
        description: '이미 존재하는 위치입니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLocations([...locations, newLocation.trim()]);
    setNewLocation('');
  }

  function removeLocation(index) {
    setLocations(locations.filter((_, i) => i !== index));
  }

  async function handleSaveLocations() {
    try {
      await saveLocations(locations);
      toast({
        title: '저장 완료',
        description: '위치 목록이 저장되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      loadJobPostings(); // 공고 목록 새로고침
    } catch (error) {
      console.error('위치 저장 오류:', error);
      toast({
        title: '오류',
        description: '위치 저장에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  function filterPostings() {
    let filtered = [...jobPostings];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (jobTypeFilter !== 'all') {
      filtered = filtered.filter(p => p.jobType === jobTypeFilter);
    }

    if (locationFilter !== 'all') {
      filtered = filtered.filter(p => p.location === locationFilter);
    }


    setFilteredPostings(filtered);
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'gray';
      case 'open': return 'blue';
      case 'closed': return 'orange';
      case 'selected': return 'green';
      case 'canceled': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return '작성중';
      case 'open': return '모집중';
      case 'closed': return '마감';
      case 'selected': return '선정완료';
      case 'canceled': return '취소됨';
      default: return status;
    }
  };

  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'manager';

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 헤더 영역 */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={textColor} mb={1}>
            구인 공고
          </Heading>
          <Text fontSize="sm" color="gray.500">
            전체 {jobPostings.length}개 | 조회 {filteredPostings.length}개
          </Text>
        </Box>
        {isAdmin && (
          <HStack spacing={3}>
            <Button
              leftIcon={<SettingsIcon />}
              variant="outline"
              onClick={onOpen}
            >
              위치 관리
            </Button>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              onClick={() => history.push('/admin/job-postings/create')}
            >
              공고 등록
            </Button>
          </HStack>
        )}
      </Flex>

      {/* 필터 영역 */}
      <Card mb={6}>
        <CardBody py={4}>
          <Grid
            templateColumns={{ sm: '1fr', md: 'repeat(3, 1fr)' }}
            gap={4}
          >
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">전체 상태</option>
              <option value="open">모집중</option>
              <option value="closed">마감</option>
              <option value="selected">선정완료</option>
              <option value="draft">작성중</option>
              <option value="canceled">취소됨</option>
            </Select>

            <Select
              value={jobTypeFilter}
              onChange={(e) => setJobTypeFilter(e.target.value)}
            >
              <option value="all">전체 유형</option>
              {jobTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Select>

            <Select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="all">전체 위치</option>
              {locations.map((location, index) => (
                <option key={index} value={location}>{location}</option>
              ))}
            </Select>

          </Grid>
        </CardBody>
      </Card>

      {/* 공고 목록 */}
      {loading ? (
        <Card>
          <CardBody>
            <Text textAlign="center" py={12}>로딩 중...</Text>
          </CardBody>
        </Card>
      ) : filteredPostings.length === 0 ? (
        <Card>
          <CardBody>
            <Flex direction="column" align="center" py={12}>
              <Text fontSize="lg" color="gray.500" mb={2}>
                등록된 공고가 없습니다
              </Text>
              {isAdmin && (
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="blue"
                  mt={4}
                  onClick={() => history.push('/admin/job-postings/create')}
                >
                  첫 공고 등록하기
                </Button>
              )}
            </Flex>
          </CardBody>
        </Card>
      ) : (
        <Grid
          templateColumns={{
            base: 'repeat(auto-fit, minmax(280px, auto))',
            md: 'repeat(auto-fit, minmax(280px, auto))',
          }}
          gap={6}
          alignItems="start"
          justifyItems="start"
          justifyContent="flex-start"
        >
          {filteredPostings.map((posting) => (
            <Card
              key={posting.id}
              _hover={{ 
                shadow: 'xl', 
                transform: 'translateY(-4px)',
                transition: 'all 0.3s'
              }}
              cursor="pointer"
              onClick={() => history.push(`/admin/job-postings/${posting.id}`)}
              borderWidth="1px"
              borderColor={borderColor}
            >
              <CardBody p={5}>
                <Stack spacing={4}>
                  <Flex justify="space-between" align="start" gap={2}>
                    <Stack direction="row" spacing={2}>
                      <Badge colorScheme={getStatusColor(posting.status)} fontSize="xs">
                        {getStatusText(posting.status)}
                      </Badge>
                      {posting.jobType && (
                        <Badge colorScheme="purple" fontSize="xs" variant="subtle">
                          {posting.jobType}
                        </Badge>
                      )}
                    </Stack>
                    {isAdmin && (
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<EditIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <MenuList>
                          <MenuItem 
                            icon={<EditIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              history.push(`/admin/job-postings/${posting.id}/edit`);
                            }}
                          >
                            수정
                          </MenuItem>
                          <MenuItem 
                            icon={<DeleteIcon />} 
                            color="red.500"
                            onClick={(e) => e.stopPropagation()}
                          >
                            삭제
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    )}
                  </Flex>

                  <Box>
                    <Heading size="md" color={textColor} mb={2} noOfLines={1}>
                      {posting.title}
                    </Heading>
                    <Text fontSize="sm" color="gray.500">
                      {truncateDescription(posting.description, 15)}
                    </Text>
                  </Box>

                  <Stack spacing={2} fontSize="sm" borderTopWidth="1px" pt={3}>
                    <Flex justify="space-between">
                      <Text color="gray.500">위치</Text>
                      <Text color={textColor} fontWeight="medium">{posting.location}</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text color="gray.500">모집</Text>
                      <Text color={textColor} fontWeight="medium">{posting.headcount}명</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text color="gray.500">급여</Text>
                      <Text color={textColor} fontWeight="medium">
                        {posting.wageType} {posting.wageAmount?.toLocaleString()}원
                      </Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text color="gray.500">기간</Text>
                      <Text color={textColor} fontSize="xs" fontWeight="medium" ml={6}>
                        {posting.periodStart} ~ {posting.periodEnd}
                      </Text>
                    </Flex>
                  </Stack>

                  {posting.status === 'open' && !isAdmin && (
                    <Button
                      colorScheme="blue"
                      size="sm"
                      w="100%"
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(`/admin/job-postings/${posting.id}`);
                      }}
                    >
                      지원하기
                    </Button>
                  )}
                </Stack>
              </CardBody>
            </Card>
          ))}
        </Grid>
      )}

      {/* 위치 관리 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>위치 관리</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <Text fontSize="sm" color="gray.600">
                구인공고에서 사용할 위치 목록을 관리합니다.
              </Text>

              {/* 위치 추가 */}
              <Flex gap={2}>
                <Input
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  placeholder="새 위치 입력 (예: GS25 생활과학관점)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addLocation();
                    }
                  }}
                />
                <Button colorScheme="blue" onClick={addLocation}>
                  추가
                </Button>
              </Flex>

              {/* 위치 목록 */}
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={2}>
                  현재 위치 목록 ({locations.length}개)
                </Text>
                <HStack spacing={2} flexWrap="wrap">
                  {locations.map((location, index) => (
                    <Tag key={index} size="lg" colorScheme="blue" borderRadius="full">
                      <TagLabel>{location}</TagLabel>
                      <TagCloseButton onClick={() => removeLocation(index)} />
                    </Tag>
                  ))}
                </HStack>
                {locations.length === 0 && (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                    등록된 위치가 없습니다
                  </Text>
                )}
              </Box>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              취소
            </Button>
            <Button colorScheme="blue" onClick={handleSaveLocations}>
              저장
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default JobPostings;

